import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';

import { useAuth } from 'hooks';
import { Tooltip } from 'components/common';
import { getActiveFilters } from 'selectors/search';
import {
  organisationEmployeeCount,
  organisationFoundationYear,
  organisationsTypeahead,
} from 'services/inputTokens';
import { tagRender } from 'components/common/TagRender';
import { renderOption } from 'components/common/OptionRender';
import { formatCurrency } from 'utils/number';
import licenseType from 'models/licenseType';
import { setFilter } from 'actions/search';

import { Filter } from '../helpers';
import FilterGroup from './FilterGroup';
import { orgOrganisationKeys } from './keys';

import { FilterSection } from './styled';

const activityFilterGroups = [
  {
    academic: orgOrganisationKeys.superOrgAcademicTotalGrants,
    industry: orgOrganisationKeys.superOrgIndustryTotalGrants,
    total: orgOrganisationKeys.superOrgTotalGrants,
    helpText:
      'Show organisations with at least this number of funding items across all contacts',
  },
  {
    academic: orgOrganisationKeys.superOrgAcademicTotalSessions,
    industry: orgOrganisationKeys.superOrgIndustryTotalSessions,
    total: orgOrganisationKeys.superOrgTotalSessions,
    helpText:
      'Show organisations with at least this number of tradeshow items across all contacts',
  },
  {
    academic: orgOrganisationKeys.superOrgAcademicTotalPublications,
    industry: orgOrganisationKeys.superOrgIndustryTotalPublications,
    total: orgOrganisationKeys.superOrgTotalPublications,
    helpText:
      'Show organisations with at least this number of publication items across all contacts',
  },
  {
    academic: orgOrganisationKeys.superOrgAcademicTotalClinicalTrials,
    industry: orgOrganisationKeys.superOrgIndustryTotalClinicalTrials,
    total: orgOrganisationKeys.superOrgTotalClinicalTrials,
    helpText:
      'Show organisations with at least this number of clinical trial items across all contacts',
  },
  {
    academic: orgOrganisationKeys.superOrgAcademicThreeYearTotalFunding,
    industry: orgOrganisationKeys.superOrgIndustryThreeYearTotalFunding,
    total: orgOrganisationKeys.superOrgThreeYearTotalFunding,
    formatter: formatCurrency,
    step: 10000,
    helpText:
      'Show organisations with at least this total combined funding, awarded to contacts, over 3 years',
  },
];

const OrgFilters = ({ activeOnly, loading }) => {
  return (
    <>
      <Filter
        filterType='selectIncludeExclude'
        filterId={orgOrganisationKeys.superOrganisationSize.key}
        placeholder='Employee Count'
        mode={'multiple'}
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        helpText='Filter results by the number of employees at an organisation'
        options={organisationEmployeeCount}
        activeOnly={activeOnly}
        disabled={loading}
      />
      <Filter
        style={{ width: '100%' }}
        filterType='select'
        filterId={orgOrganisationKeys.superOrganisationFoundedYear.key}
        placeholder='Year Founded'
        allowClear
        showSearch
        options={organisationFoundationYear}
        activeOnly={activeOnly}
        disabled={loading}
      />
      {/*<Filter*/}
      {/*  filterType='number'*/}
      {/*  filterId='minRevenue'*/}
      {/*  formatter={formatCurrency}*/}
      {/*  placeholder='Min. Revenue (Yearly)'*/}
      {/*  step={10000}*/}
      {/*  min={0}*/}
      {/*  helpText='Filter results by the minimum yearly revenue for an organisation'*/}
      {/*/>*/}
      {/*<Filter*/}
      {/*  filterType='number'*/}
      {/*  filterId='maxRevenue'*/}
      {/*  formatter={formatCurrency}*/}
      {/*  placeholder='Max. Revenue (Yearly)'*/}
      {/*  step={10000}*/}
      {/*  min={0}*/}
      {/*  helpText='Filter results by the maximum yearly revenue for an organisation'*/}
      {/*/>*/}
    </>
  );
};

const Organisation = ({ loading }) => {
  const dispatch = useDispatch();
  const { hasLicense } = useAuth();

  const hasIndustryOnlyLicense =
    hasLicense([licenseType.industry], true) &&
    !hasLicense([licenseType.academic], true);

  const hasAcademicOnlyLicense =
    hasLicense([licenseType.academic], true) &&
    !hasLicense([licenseType.industry], true);

  const activeFilters = useSelector(
    getActiveFilters(Object.keys(orgOrganisationKeys))
  );

  // For each activity filter group, determine the key to use based on licence and the keys to clear.
  const resolvedFilters = useMemo(
    () =>
      activityFilterGroups.map(group => {
        if (hasIndustryOnlyLicense) {
          return {
            ...group,
            activeKey: group.industry.key,
            invalidKeys: [group.academic.key, group.total.key],
          };
        }
        if (hasAcademicOnlyLicense) {
          return {
            ...group,
            activeKey: group.academic.key,
            invalidKeys: [group.industry.key, group.total.key],
          };
        }
        return {
          ...group,
          activeKey: group.total.key,
          invalidKeys: [],
        };
      }),
    [hasIndustryOnlyLicense, hasAcademicOnlyLicense]
  );

  // Remove filters that are not applicable based on the license type.
  // This can occur when switching between users with different licenses.
  useEffect(() => {
    resolvedFilters.forEach(({ invalidKeys }) => {
      invalidKeys.forEach(key => {
        if (activeFilters.includes(key)) {
          dispatch(setFilter(key, ''));
        }
      });
    });
  }, [resolvedFilters, activeFilters, dispatch]);

  // By default, the checkbox is disabled until a selection is added to the Organisations filter (either an include or exclude action).
  const isExactNameSearchDisabled = useMemo(() => {
    if (!activeFilters || !Array.isArray(activeFilters)) return false;
    return !activeFilters.some(
      f => f === orgOrganisationKeys.organisationName.key
    );
  }, [activeFilters]);

  return (
    <FilterGroup
      icon='company'
      title='Organisation'
      activeKeys={activeFilters}
      sectionKey='organisation'
    >
      <>
        <Filter
          filterType='selectIncludeExclude'
          placeholder='Organisations'
          mode={'multiple'}
          style={{ width: '100%' }}
          renderOption={renderOption}
          tagRender={tagRender}
          showSearch={true}
          filterId={orgOrganisationKeys.organisationName.key}
          options={organisationsTypeahead}
          disabled={loading}
        />
        <Tooltip
          title='This limits results to organisations with an exact name match.'
          placement='right'
        >
          <Filter
            filterType='checkbox'
            defaultChecked={false}
            filterId={orgOrganisationKeys.organisationNameExactMatch.key}
            disabled={loading || isExactNameSearchDisabled}
            labelText='Search exact names only'
          />
        </Tooltip>
        <Filter
          filterType='token'
          placeholder='Email Domains - Max 10'
          filterId={orgOrganisationKeys.email.key}
          tokenLimit={10}
          disabled={loading}
          includeExclude
        />
        <OrgFilters loading={loading} />
        <Filter
          filterType='token'
          placeholder='Organisation Description'
          filterId={orgOrganisationKeys.organisationDescription.key}
          disabled={loading}
          includeExclude
        />
        <Filter
          filterType='checkbox'
          filterId={orgOrganisationKeys.recentlyAddedOrg.key}
          disabled={loading}
          labelText='Added within last month'
        />
      </>
      <>
        <FilterSection>Organisation Metrics</FilterSection>
        {resolvedFilters.map(filter => (
          <Filter
            key={filter.activeKey}
            filterType='number'
            filterId={filter.activeKey}
            placeholder={orgOrganisationKeys[filter.activeKey].description}
            min={0}
            disabled={loading}
            helpText={filter.helpText}
            {...(filter.formatter && { formatter: filter.formatter })}
            {...(filter.step && { step: filter.step })}
          />
        ))}
      </>
    </FilterGroup>
  );
};

Organisation.propTypes = {
  loading: PropTypes.bool,
};

export default Organisation;
